if (!customElements.get('editorial-story-tabs')) {
  class EditorialStoryTabs extends HTMLElement {
    constructor() {
      super();
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
    }

    connectedCallback() {
      this.buttons = Array.from(this.querySelectorAll('.editorial-story-tabs__toggle'));
      this.items = Array.from(this.querySelectorAll('.editorial-story-tabs__item'));
      this.panels = Array.from(this.querySelectorAll('.editorial-story-tabs__panel'));
      this.sliders = Array.from(this.querySelectorAll('slide-show'));

      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.reduceMotionQuery.matches;
      this.autoplayDuration = Number(this.dataset.autoplaySeconds || 5) * 1000;

      this.autoplayPaused = false;
      this.autoplayFrame = null;

      this.activeIndex = this.getInitialIndex();

      // 绑定事件
      this.buttons.forEach((button, index) => {
        button.addEventListener('click', () => {
          if (!this.canHover()) this.activate(index);
        });

        button.addEventListener('mouseenter', () => {
          if (this.canHover()) this.activate(index, { focus: false });
        });

        button.addEventListener('focus', () => {
          this.activate(index, { focus: false });
        });

        button.addEventListener('keydown', (event) => this.onKeyDown(event, index));
      });

      // Shopify 编辑器支持
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const item = event.target.closest('.editorial-story-tabs__item');
          const index = this.items.indexOf(item);
          if (index > -1) this.activate(index, { focus: false });
        });
      }

      this.activate(this.activeIndex, { focus: false, restartAutoplay: false });
      this.setupAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.removeEventListener('mouseenter', this.onPointerEnter);
      this.removeEventListener('mouseleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    canHover() {
      return this.hoverQuery.matches;
    }

    getInitialIndex() {
      const activeItem = this.items.findIndex((item) => item.classList.contains('is-active'));
      return activeItem > -1 ? activeItem : 0;
    }

    onKeyDown(event, index) {
      if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();

      let nextIndex = index;

      if (event.key === 'ArrowDown') nextIndex = (index + 1) % this.buttons.length;
      if (event.key === 'ArrowUp') nextIndex = (index - 1 + this.buttons.length) % this.buttons.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.buttons.length - 1;

      this.activate(nextIndex);
    }

    // ======================
    // 自动播放
    // ======================
    setupAutoplay() {
      if (!this.autoplayEnabled || this.buttons.length < 2) return;

      this.addEventListener('mouseenter', this.onPointerEnter);
      this.addEventListener('mouseleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.resumeAutoplay();
    }

    onPointerEnter() { this.pauseAutoplay(); }
    onPointerLeave() { this.resumeAutoplay(); }
    onFocusIn() { this.pauseAutoplay(); }

    onFocusOut() {
      requestAnimationFrame(() => {
        if (!this.contains(document.activeElement)) this.resumeAutoplay();
      });
    }

    onVisibilityChange() {
      document.hidden ? this.pauseAutoplay() : this.resumeAutoplay();
    }

    pauseAutoplay() {
      this.autoplayPaused = true;
      this.stopAutoplay();
    }

    resumeAutoplay() {
      this.autoplayPaused = false;
      this.startAutoplay();
    }

    startAutoplay() {
      if (this.autoplayPaused || !this.autoplayEnabled) return;

      this.stopAutoplay();

      this.autoplayStartedAt = performance.now();
      this.autoplayFrame = requestAnimationFrame((t) => this.tickAutoplay(t));
    }

    stopAutoplay() {
      if (this.autoplayFrame) {
        cancelAnimationFrame(this.autoplayFrame);
        this.autoplayFrame = null;
      }
    }

    tickAutoplay(timestamp) {
      if (this.autoplayPaused) return;

      const elapsed = timestamp - this.autoplayStartedAt;

      if (elapsed >= this.autoplayDuration) {
        const nextIndex = (this.activeIndex + 1) % this.buttons.length;
        this.activate(nextIndex, { focus: false });
        return;
      }

      this.autoplayFrame = requestAnimationFrame((t) => this.tickAutoplay(t));
    }

    // ======================
    // 核心切换（🔥已修复）
    // ======================
    activate(index, options = {}) {
      const { focus = true, restartAutoplay = true } = options;

      const activeButton = this.buttons[index];
      if (!activeButton) return;

      this.activeIndex = index;

      // 切换文字
      this.items.forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
      });

      this.buttons.forEach((btn, i) => {
        const active = i === index;
        btn.setAttribute('aria-expanded', active);
        btn.setAttribute('aria-selected', active);
        btn.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });

      if (focus) activeButton.focus();

      // ======================
      // 🔥 图片同步（最终稳定版）
      // ======================
      this.sliders.forEach((slider) => {
        const slides = slider.querySelectorAll('.carousel__slide');

        slides.forEach((slide, i) => {
          slide.style.display = i === index ? 'block' : 'none';
        });
      });

      // autoplay
      if (restartAutoplay && this.autoplayEnabled && !this.autoplayPaused) {
        this.startAutoplay();
      }
    }
  }

  customElements.define('editorial-story-tabs', EditorialStoryTabs);
}